import $ from 'jquery';

/**
 * Video Slider Widget JS Handler Class.
 */
class VideoSliderWidgetHandler extends elementorModules.frontend.handlers.SwiperBase {
	/**
	 * Get Swiper Settings
	 *
	 * @return {Object} An object of swiper settings.
	 */
	getSwiperSettings() {
		return {
			slidesPerView: 1,
			effect: 'fade',
		};
	}

	/**
	 * Get Default Settings
	 *
	 * @return {Object} An object of default settings.
	 */
	getDefaultSettings() {
		return {
			selectors: {
				carousel: '.video-slider__slides',
				slideContent: '.swiper-slide',
				videoWrapper: '.video-slider__embed',
				pagination: '.video-slider__pagination',
				paginationItem: '.video-slider__page',
				pauseButton: '.video-slider__controls',
			},
		};
	}

	/**
	 * Get Default Elements
	 *
	 * @return {Object} An object of jquery elements.
	 */
	getDefaultElements() {
		const selectors = this.getSettings('selectors');

		const elements = {
			$videoWrapper: this.$element.find(selectors.videoWrapper),
			$pagination: this.$element.find(selectors.pagination),
			$paginationItem: this.$element.find(selectors.paginationItem),
			$pauseButton: this.$element.find(selectors.pauseButton),
			$swiperContainer: this.$element.find(selectors.carousel),
		};

		elements.$slides = elements.$swiperContainer.find(selectors.slideContent);

		return elements;
	}

	/**
	 * Bind Events
	 *
	 * Runs after the widget is loaded to bind events to the element.
	 */
	bindEvents() {
		const $pauseButton = this.elements.$pauseButton;
		const $paginationItem = this.elements.$paginationItem;

		const classes = {
			paused: 'js-video-is-paused',
			playing: 'js-video-is-playing',
		};

		$pauseButton.on('click', () => {
			if ($pauseButton.hasClass(classes.playing)) {
				this.players[this.activeSlide].pause();
				$pauseButton.removeClass(classes.playing);
				$pauseButton.addClass(classes.paused);
			} else {
				this.players[this.activeSlide].play();
				$pauseButton.removeClass(classes.paused);
				$pauseButton.addClass(classes.playing);
			}
		});

		$paginationItem.on('click', (e) => {
			const index = $paginationItem.index($(e.currentTarget));
			this.changeSlide(index);
			if ($pauseButton.hasClass(classes.paused)) {
				$pauseButton.removeClass(classes.paused);
				$pauseButton.addClass(classes.playing);
			}
		});
	}

	/**
	 * On Init
	 *
	 * Runs when the widget is loaded and initialized in the frontend.
	 */
	async onInit(...args) {
		super.onInit(...args);

		this.swiper = null;
		this.swiperControl = null;
		const slideTotal = this.elements.$slides.length;

		if (this.elements.$swiperContainer.length && 2 <= slideTotal) {
			const Swiper = elementorFrontend.utils.swiper;

			this.swiperControl = await new Swiper(this.elements.$pagination, {
				slidesPerView: 1.5,
				spaceBetween: 36,
				centeredSlides: true,
				centeredSlidesBounds: true,
				breakpoints: {
					768: {
						slidesPerView: slideTotal >= 4 ? 4 : slideTotal,
					},
					1200: {
						slidesPerView: slideTotal >= 6 ? 6 : slideTotal,
					},
				},
			});

			this.swiper = await new Swiper(this.elements.$swiperContainer, this.getSwiperSettings());

			// Expose the swiper instance in the frontend
			this.elements.$swiperContainer.data('swiper', this.swiper);
			this.elements.$pagination.data('swiper', this.swiperControl);
		}

		this.player = null;
		this.activeSlide = 0;
		this.players = [];

		let apiProvider;
		const $wrapper = this.elements.$videoWrapper;
		const settings = this.getElementSettings('slides');
		const self = this;

		switch ($wrapper.attr('data-provider')) {
			case 'vimeo':
				apiProvider = elementorFrontend.utils.vimeo;
				apiProvider.onApiReady((Vimeo) => {
					$wrapper.each(function () {
						let initialized = false;
						const $this = $(this);
						const player = new Vimeo.Player($this[0], {
							autoplay: false,
							autopause: false,
							background: true,
							byline: false,
							controls: false,
							loop: false,
							muted: true,
							portrait: false,
							title: false,
							url: $this.data('media-url'),
						});

						// Background videos autoplay by default but since this
						// is a slideshow we pause everything at first and we'll
						// control when the first slide plays with an
						// intersection observer later.
						player.on('play', () => {
							if (!initialized) {
								player.pause();
								initialized = true;
							}
						});

						// When a video ends progress to the next automatically.
						player.on('ended', () => {
							const index = self.activeSlide >= settings.length - 1 ? 0 : self.activeSlide + 1;
							self.changeSlide(index);
						});

						// Add the players to an array for later access.
						self.players.push(player);
					});

					// Play the first video when it's in the viewport.
					this.players[0].ready().then(() => {
						const intersectionObserver = elementorModules.utils.Scroll.scrollObserver({
							callback: (event) => {
								if (event.isInViewport) {
									intersectionObserver.unobserve(this.elements.$pagination[0]);
									this.players[0].play();
								}
							},
						});

						intersectionObserver.observe(this.elements.$pagination[0]);
					});
				});
				break;
		}
	}

	/**
	 * Change slide.
	 *
	 * @param {Number} index
	 */
	changeSlide(index) {
		const $paginationItem = this.elements.$paginationItem;

		// Stop the current video.
		this.players[this.activeSlide].pause();
		this.players[this.activeSlide].setCurrentTime(0);

		// Update the active slide.
		this.activeSlide = index;

		// Toggle paginationItem classes.
		$paginationItem.removeClass('is-active');
		$paginationItem.eq(index).addClass('is-active');

		// Start the next video.
		this.players[index].play();

		// Progress swiper if available.
		if (this.swiper) {
			this.swiper.slideTo(index);
			this.swiperControl.slideTo(index);
		}
	}
}

/**
 * Register JS Handler for the Video Slider Widget
 */
elementorFrontend.elements.$window.on('elementor/frontend/init', () => {
	elementorFrontend.elementsHandler.attachHandler('video-slider', VideoSliderWidgetHandler);
});
